import { Link } from 'react-router-dom'
import { useSerieDeIndicadores } from '../../api/queries/indicadores'
import { AvisoDeErro, Esqueleto, EstadoVazio } from '../../components/Estados'
import { Explicacao } from '../../components/Explicacao'
import { IconeIndicador } from '../../components/Icones'
import { TEXTOS } from '../../lib/glossario'
import { EvolucaoDoDesconto } from './EvolucaoDoDesconto'
import { TabelaDaSerie } from './TabelaDaSerie'
import { versoesDeCalculo } from './serie'

/**
 * Os seis indicadores do Anexo VIII, mês a mês.
 *
 * **Responde outra pergunta que a do fechamento.** O fechamento diz quanto vale um mês; esta tela
 * diz para onde o contrato está indo. O Anexo VIII prevê advertência para a apuração abaixo do
 * limite e multa para a **reincidência** — e reincidência não se enxerga um mês por vez.
 *
 * @returns a série histórica
 */
export function PaginaSerieDeIndicadores() {
  const t = TEXTOS.indicadores
  const { data: linhas, isPending, isError, error } = useSerieDeIndicadores()

  return (
    <div className="space-y-5">
      <header>
        <div className="flex items-center gap-1.5">
          <h1 className="titulo-da-pagina">{t.titulo}</h1>
          <Explicacao termo="serieDeIndicadores" />
        </div>
        <p className="mt-1 max-w-prose text-sm text-slate-600">{t.ajuda}</p>
      </header>

      {isPending && <Esqueleto linhas={6} />}
      {isError && <AvisoDeErro erro={error} />}

      {linhas && linhas.length === 0 && (
        <EstadoVazio
          icone={<IconeIndicador className="h-9 w-9" />}
          titulo={t.semCompetencias.titulo}
          ajuda={t.semCompetencias.ajuda}
          acao={
            <Link to="/fiscalizacao" className="text-sm font-medium text-sky-800 hover:underline">
              {t.semCompetencias.acao}
            </Link>
          }
        />
      )}

      {linhas && linhas.length > 0 && (
        <>
          <AvisoDeComparabilidade versoes={versoesDeCalculo(linhas)} />
          <EvolucaoDoDesconto linhas={linhas} />
          <TabelaDaSerie linhas={linhas} />
          <p className="max-w-prose text-xs text-slate-500">{t.semNumeroNaoEhZero}</p>
        </>
      )}
    </div>
  )
}

/**
 * O aviso de que dois meses da série não se comparam diretamente.
 *
 * Some quando há uma versão só, que é o caso normal. Aparece quando a fórmula de algum indicador
 * mudou no meio da série — e aí uma linha que sobe pode ser a regra que mudou, não o desempenho.
 *
 * @param props as versões de cálculo presentes
 * @returns o aviso, ou nada
 */
function AvisoDeComparabilidade({ versoes }: { versoes: string[] }) {
  if (versoes.length < 2) {
    return null
  }
  return (
    <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900 ring-1 ring-inset ring-amber-200">
      {TEXTOS.indicadores.versoesDiferentes(versoes.join(', '))}
    </p>
  )
}
